import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  History,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Calendar,
  FileText,
  Sparkles,
  Loader2,
} from "lucide-react";
import { toast } from "react-hot-toast";
import documentSearchService, {
  SearchHistoryItem,
  SearchResponseHistoryItem,
} from "../../services/document-search.service";
import MarkdownRenderer from "./MarkdownRenderer";

interface QueryHistoryTabProps {
  documentStoreId: string;
}

export interface QueryHistoryTabRef {
  refreshHistory: () => void;
}

const QueryHistoryTab = forwardRef<QueryHistoryTabRef, QueryHistoryTabProps>(
  ({ documentStoreId }, ref) => {
    const [historyList, setHistoryList] = useState<SearchHistoryItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [detailsCache, setDetailsCache] = useState<
      Record<string, SearchResponseHistoryItem[]>
    >({});
    const [loadingDetails, setLoadingDetails] = useState<Set<string>>(
      new Set()
    );
    const [regenerating, setRegenerating] = useState<Set<string>>(new Set());
    const [skip, setSkip] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const limit = 10;

    useEffect(() => {
      fetchHistoryList();
    }, [documentStoreId, skip]);

    const fetchHistoryList = async () => {
      setIsLoading(true);
      try {
        const response = await documentSearchService.getSearchHistoryList(
          documentStoreId,
          skip,
          limit
        );
        if (response.success) {
          setHistoryList(response.data.Document_search_history_list);
          setTotalCount(response.data.total_count);
        } else {
          toast.error("Failed to fetch search history");
        }
      } catch (error: any) {
        console.error("Error fetching search history:", error);
        toast.error(
          error.response?.data?.message || "Failed to fetch search history"
        );
      } finally {
        setIsLoading(false);
      }
    };

    // Expose refresh method to parent component
    useImperativeHandle(ref, () => ({
      refreshHistory: () => {
        setSkip(0); // Reset to first page
        fetchHistoryList();
      },
    }));

    const toggleAccordion = async (itemId: string) => {
      const newExpandedItems = new Set(expandedItems);

      if (expandedItems.has(itemId)) {
        newExpandedItems.delete(itemId);
      } else {
        newExpandedItems.add(itemId);

        // Fetch details if not already cached
        if (!detailsCache[itemId]) {
          setLoadingDetails(new Set(loadingDetails).add(itemId));
          try {
            const response = await documentSearchService.getSearchHistoryDetail(
              itemId
            );
            if (response.success) {
              setDetailsCache({
                ...detailsCache,
                [itemId]: response.data.search_response_history,
              });
            } else {
              toast.error("Failed to fetch query details");
            }
          } catch (error: any) {
            console.error("Error fetching query details:", error);
            toast.error(
              error.response?.data?.message || "Failed to fetch query details"
            );
          } finally {
            setLoadingDetails((prev) => {
              const newSet = new Set(prev);
              newSet.delete(itemId);
              return newSet;
            });
          }
        }
      }

      setExpandedItems(newExpandedItems);
    };

    const handleRegenerate = async (itemId: string) => {
      setRegenerating(new Set(regenerating).add(itemId));
      try {
        const response = await documentSearchService.regenerateSearch(
          documentStoreId,
          itemId
        );
        if (response.success) {
          toast.success("Search regenerated successfully");

          // Refresh the details
          const detailResponse =
            await documentSearchService.getSearchHistoryDetail(itemId);
          if (detailResponse.success) {
            setDetailsCache({
              ...detailsCache,
              [itemId]: detailResponse.data.search_response_history,
            });
          }
        } else {
          toast.error("Failed to regenerate search");
        }
      } catch (error: any) {
        console.error("Error regenerating search:", error);
        toast.error(
          error.response?.data?.message || "Failed to regenerate search"
        );
      } finally {
        setRegenerating((prev) => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      }
    };

    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    };

    const handleLoadMore = () => {
      setSkip(skip + limit);
    };

    const handleLoadPrevious = () => {
      setSkip(Math.max(0, skip - limit));
    };

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="relative w-12 h-12">
              <div className="absolute inset-0 rounded-full border-2 border-t-blue-600 border-r-transparent border-b-blue-400 border-l-transparent animate-spin"></div>
              <div className="absolute inset-2 rounded-full border-2 border-t-indigo-400 border-r-transparent border-b-indigo-600 border-l-transparent animate-[spin_3s_linear_infinite_reverse]"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <History className="w-5 h-5 text-blue-600 animate-pulse" />
              </div>
            </div>
            <p className="text-sm text-slate-600">Loading query history...</p>
          </div>
        </div>
      );
    }

    if (historyList.length === 0) {
      return (
        <div className="bg-gradient-to-br from-slate-50 to-blue-50/20 rounded-lg p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 mb-3">
            <History className="h-8 w-8 text-blue-600" />
          </div>
          <h3 className="text-sm font-bold text-slate-900 mb-2">
            No Query History
          </h3>
          <p className="text-xs text-slate-600 max-w-md mx-auto">
            Your search history will appear here once you start making queries.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <History className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-sm font-bold text-slate-800">Query History</h2>
            <span className="text-xs text-slate-500">
              ({totalCount} {totalCount === 1 ? "query" : "queries"})
            </span>
          </div>
        </div>

        <div className="space-y-2">
          {historyList.map((item) => {
            const isExpanded = expandedItems.has(item._id);
            const isLoadingDetail = loadingDetails.has(item._id);
            const isRegenerating = regenerating.has(item._id);
            const details = detailsCache[item._id];

            return (
              <div
                key={item._id}
                className="border border-slate-200 rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow"
              >
                {/* Accordion Header */}
                <div
                  onClick={() => toggleAccordion(item._id)}
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-slate-600" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                        <p className="text-sm font-semibold text-slate-800 truncate">
                          {item.search_query}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(item.createdAt)}
                        </span>
                        <span className="text-slate-300">•</span>
                        <span>{item.folder_name}</span>
                        <span className="text-slate-300">•</span>
                        <span>
                          {item.result_count}{" "}
                          {item.result_count === 1 ? "result" : "results"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRegenerate(item._id);
                    }}
                    disabled={isRegenerating}
                    className="flex-shrink-0 ml-3 p-2 rounded-lg border border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Regenerate search"
                  >
                    <RefreshCw
                      className={`w-3.5 h-3.5 text-blue-600 ${
                        isRegenerating ? "animate-spin" : ""
                      }`}
                    />
                  </button>
                </div>

                {/* Accordion Content */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-slate-50">
                    {isLoadingDetail ? (
                      <div className="p-4 flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                        <span className="text-sm text-slate-600">
                          Loading details...
                        </span>
                      </div>
                    ) : details && details.length > 0 ? (
                      <div className="p-3 space-y-2">
                        {details.map((detail, index) => (
                          <div
                            key={detail.id}
                            className="bg-white rounded-lg border border-slate-200 p-3"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                                  <Sparkles className="w-3.5 h-3.5 text-white" />
                                </div>
                                <span className="text-xs font-semibold text-slate-700">
                                  Result #{index + 1}
                                </span>
                              </div>
                              <span className="text-xs text-slate-500">
                                {formatDate(detail.createdAt)}
                              </span>
                            </div>
                            <MarkdownRenderer content={detail.search_result} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No results available
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalCount > limit && (
          <div className="flex items-center justify-between pt-3 border-t border-slate-200">
            <button
              onClick={handleLoadPrevious}
              disabled={skip === 0}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Previous
            </button>
            <span className="text-sm text-slate-600">
              Showing {skip + 1} - {Math.min(skip + limit, totalCount)} of{" "}
              {totalCount}
            </span>
            <button
              onClick={handleLoadMore}
              disabled={skip + limit >= totalCount}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  }
);

QueryHistoryTab.displayName = "QueryHistoryTab";

export default QueryHistoryTab;
